import { useEffect, useState } from "react";
import { CalendarDays, Shield, ArrowRight, Info, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import SubscriptionCard from "../components/SubscriptionCard";
import PageHero from "../components/PageHero";
import { toast } from "sonner";
import { api, http } from "../lib/api";
import { VAT_RATES } from "../lib/format";
import { useAuth } from "../contexts/AuthContext";

const MONTHS = [
  { n: 1, label: "Janvier" }, { n: 2, label: "Février" }, { n: 3, label: "Mars" },
  { n: 4, label: "Avril" }, { n: 5, label: "Mai" }, { n: 6, label: "Juin" },
  { n: 7, label: "Juillet" }, { n: 8, label: "Août" }, { n: 9, label: "Septembre" },
  { n: 10, label: "Octobre" }, { n: 11, label: "Novembre" }, { n: 12, label: "Décembre" },
];

export default function SettingsPage() {
  const [s, setS] = useState({
    establishment_name: "", default_vat_rate: 10, currency: "EUR",
    open_months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], alert_email: "",
  });
  const [planState, setPlanState] = useState(null);
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const inDemoMode = !!user?.demo_mode;

  useEffect(() => {
    api.settings().then((d) => setS({ ...d, open_months: d.open_months || [1,2,3,4,5,6,7,8,9,10,11,12], alert_email: d.alert_email || "" }));
    http.get("/plan/me").then((r) => setPlanState(r.data)).catch(() => setPlanState(null));
  }, []);

  const toggleMonth = (n) => {
    const cur = s.open_months || [];
    const next = cur.includes(n) ? cur.filter((m) => m !== n) : [...cur, n].sort((a, b) => a - b);
    setS({ ...s, open_months: next });
  };

  const setAll = () => setS({ ...s, open_months: [1,2,3,4,5,6,7,8,9,10,11,12] });
  const setNone = () => setS({ ...s, open_months: [] });

  const save = async () => {
    await api.updateSettings({ ...s, default_vat_rate: Number(s.default_vat_rate) });
    toast.success("Paramètres enregistrés", { description: `Calculs annuels basés sur ${s.open_months.length} mois d'ouverture.` });
  };

  const reset = async () => {
    if (!window.confirm("Effacer toutes les données ?")) return;
    await api.resetDemo(); toast.success("Données effacées");
  };
  const load = async () => {
    await api.loadDemo(); toast.success("Démo rechargée"); window.location.reload();
  };
  // Feb 2026 — « Quitter la démo » : efface uniquement les données de
  // démonstration pour repartir sur une base vierge de production. Même
  // endpoint que `reset` mais avec un message plus explicite pour l'usage.
  const quitDemo = async () => {
    if (!window.confirm("Quitter la démo et repartir avec une base vierge ? Les fournisseurs, produits, factures et recettes de démonstration seront supprimés définitivement.")) return;
    try {
      await api.exitDemo();
      await refresh();
      toast.success("Démo quittée", { description: "Vous pouvez maintenant importer vos propres factures." });
      window.location.reload();
    } catch {
      toast.error("Impossible de quitter la démo.");
    }
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <PageHero
        eyebrow="Configuration"
        title="Paramètres"
        subtitle="Établissement, TVA, e-mails et abonnement."
        pattern="grid"
      />

      {inDemoMode && (
        <Card
          data-testid="demo-mode-banner"
          className="rounded-lg border-[#FF9F1C]/50 bg-gradient-to-br from-[#FFF7E5] to-white"
        >
          <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-md bg-[#FF9F1C]/15 text-[#F58F06] flex items-center justify-center shrink-0">
                <Info className="w-4 h-4" />
              </div>
              <div>
                <div className="font-heading font-extrabold text-[15px] text-[#0B1F33]">
                  Vous explorez RentaScan en mode démo
                </div>
                <p className="text-[12.5px] text-[#5E6875] mt-0.5 max-w-[560px]">
                  Fournisseurs, produits, factures et recettes de démonstration sont chargés pour vous familiariser avec l'interface. Quittez la démo quand vous êtes prêt à travailler sur vos propres données.
                </p>
              </div>
            </div>
            <Button
              data-testid="demo-exit-btn"
              onClick={quitDemo}
              className="gap-2 bg-[#0B1F33] hover:bg-[#0F2A45] text-white"
            >
              <LogOut className="w-4 h-4" /> Quitter la démo
            </Button>
          </CardContent>
        </Card>
      )}

      <SubscriptionCard />

      <Card className="rounded-lg border-slate-200">
        <CardContent className="p-5 space-y-4">
          <div>
            <Label>Nom de l'établissement</Label>
            <Input data-testid="settings-name" value={s.establishment_name || ""} onChange={(e) => setS({ ...s, establishment_name: e.target.value })} />
          </div>
          <div>
            <Label>Taux de TVA par défaut</Label>
            <Select value={String(s.default_vat_rate)} onValueChange={(v) => setS({ ...s, default_vat_rate: Number(v) })}>
              <SelectTrigger data-testid="settings-vat"><SelectValue /></SelectTrigger>
              <SelectContent>{VAT_RATES.map((v) => <SelectItem key={v.value} value={String(v.value)}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Devise</Label>
            <Input value={s.currency || "EUR"} disabled />
          </div>
          <div>
            <Label>Email pour alertes hausse fournisseur & résumé hebdo</Label>
            <Input
              data-testid="settings-alert-email"
              type="email"
              placeholder="ex : gerant@monbistro.fr"
              value={s.alert_email || ""}
              onChange={(e) => setS({ ...s, alert_email: e.target.value })}
            />
            <p className="text-xs text-slate-500 mt-1">
              Reçoit les alertes de hausse et le <strong>résumé hebdo</strong> automatique chaque lundi matin (marge, top hausses, reco RentaScan).
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid="settings-send-weekly-now"
                onClick={async () => {
                  try {
                    await api.sendWeeklySummary();
                    toast.success("Résumé envoyé", { description: `À : ${s.alert_email}` });
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Envoi échoué");
                  }
                }}
                className="rounded-full">
                Envoyer un résumé de test maintenant
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-lg border-slate-200">
        <CardContent className="p-5">
          <Button data-testid="settings-save" onClick={save} className="bg-[#2878FF] hover:bg-[#1769E0] text-white">Enregistrer les paramètres</Button>
        </CardContent>
      </Card>

      <Card className="rounded-lg border-slate-200">
        <CardContent className="p-5">
          <h3 className="font-heading font-semibold text-slate-900">Données</h3>
          <p className="text-sm text-slate-500 mt-1 mb-3">Recharger la démo ou effacer les données.</p>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="settings-load-demo" onClick={load} className="bg-slate-900 hover:bg-slate-800 text-white">Recharger la démo</Button>
            <Button data-testid="settings-quit-demo" onClick={quitDemo} variant="outline" className="text-[#0B1F33] border-[#0B1F33] hover:bg-[#F4F5EF]">Quitter la démo</Button>
            <Button data-testid="settings-reset" onClick={reset} variant="outline" className="text-red-700 border-red-200 hover:bg-red-50">Effacer toutes les données</Button>
          </div>
        </CardContent>
      </Card>

      {/* Administration — visible UNIQUEMENT pour les comptes super_admin.
          Les autres utilisateurs ne verront ni la section ni le bouton, et l'accès direct
          à /admin/simulateur est protégé côté serveur (403) via is_super_admin(). */}
      {planState?.is_super_admin && (
        <Card className="rounded-lg border-[#FFD166] bg-[#FFF7E5]" data-testid="settings-admin-section">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-[#7A4C0B]" />
              <h3 className="font-heading font-semibold text-[#7A4C0B]">Administration</h3>
              <Badge className="bg-[#FF9F1C] text-[#0B1F33] hover:bg-[#FF9F1C] border-0 text-[10px] uppercase tracking-widest font-bold">super admin</Badge>
            </div>
            <p className="text-sm text-[#7A4C0B]/85 mb-3">
              Simulateur d'abonnement — testez les restrictions Essentiel / Pro / Intégral avec vos données existantes.
              Aucune facturation Stripe n'est modifiée.
              {planState?.simulated && (
                <> Vous êtes actuellement en mode simulation <b>{planState.effective_plan}</b>.</>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => navigate("/admin/simulateur")}
                data-testid="settings-open-simulator"
                className="bg-[#0B1F33] hover:bg-[#0B1F33]/90 text-white gap-1.5"
              >
                Ouvrir le simulateur d'abonnement <ArrowRight className="w-4 h-4" />
              </Button>
              {planState?.simulated && (
                <Button
                  variant="outline"
                  data-testid="settings-exit-simulation"
                  onClick={async () => {
                    try {
                      await http.post("/admin/simulate/reset");
                      const r = await http.get("/plan/me");
                      setPlanState(r.data);
                      toast.success("Retour à l'abonnement réel");
                    } catch { toast.error("Échec"); }
                  }}
                  className="border-[#0B1F33] text-[#0B1F33]"
                >
                  Revenir à mon abonnement réel
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
